import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Users } from 'lucide-react';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import { getDataTypeIcon } from './UserPropertyFilterModule';
import { GROUP_STANDARD_PROPERTIES, getDefaultGroupOperator } from '../config/groupFilterProperties';
import { DEFAULT_WHO_TIME_FILTER } from '../utils/whoTimeFilterUtils';

function generateId() {
  return `gf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function mapApiPropType(t) {
  if (!t) return 'string';
  const x = String(t).toLowerCase();
  if (x === 'array') return 'list';
  return x;
}

/**
 * Group filter picker: Events (WHO) / Properties (WHERE) / Cohorts — matches user filter UX.
 */
const GroupPropertyFilterPopup = ({
  isOpen,
  onClose,
  triggerRef,
  teamId,
  groupKey,
  existingFilter,
  onAdd,
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState('events');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [groupProperties, setGroupProperties] = useState([]);
  const [eventNames, setEventNames] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const popupRef = useRef(null);

  const getNumericTeamId = () => {
    let id = typeof teamId === 'object' && teamId !== null
      ? parseInt(teamId._id || teamId.id, 10)
      : parseInt(teamId, 10);
    if (typeof teamId === 'string' && Number.isNaN(id)) {
      try {
        const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        id = parseInt(td._id || td.id, 10);
      } catch (_) {
        /* ignore */
      }
    }
    return Number.isNaN(id) ? null : id;
  };

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({
        top: rect.bottom + scrollY + 8,
        left: rect.left + scrollX,
      });
    }
  }, [isOpen, triggerRef]);

  useEffect(() => {
    if (!isOpen) return;
    if (existingFilter) {
      const src = existingFilter.propertySource || existingFilter.category;
      if (src === 'event') setSelectedNavItem('events');
      else if (src === 'cohort') setSelectedNavItem('cohorts');
      else setSelectedNavItem('properties');
    } else {
      setSelectedNavItem('events');
    }
    setSearchTerm('');
  }, [isOpen, existingFilter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || selectedNavItem !== 'properties' || !teamId || !groupKey) return;
    setLoadingProps(true);
    axiosInstance
      .get('/group-identities/properties', { params: { team_id: teamId, group_key: groupKey } })
      .then((r) => {
        const raw = r.data?.properties || [];
        const custom = raw.map((item) => {
          const key = typeof item === 'string' ? item : item.key;
          const type = typeof item === 'object' && item ? mapApiPropType(item.type) : 'string';
          return {
            name: key,
            label: key,
            type,
            source: 'group',
          };
        });
        const stdNames = new Set(GROUP_STANDARD_PROPERTIES.map((p) => p.name));
        const merged = [
          ...GROUP_STANDARD_PROPERTIES.map((p) => ({
            name: p.name,
            label: p.label,
            type: p.type,
            source: 'group',
            isStandard: true,
          })),
          ...custom.filter((p) => p.name && !stdNames.has(p.name)),
        ].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
        setGroupProperties(merged);
      })
      .catch(() => setGroupProperties(
        GROUP_STANDARD_PROPERTIES.map((p) => ({
          name: p.name,
          label: p.label,
          type: p.type,
          source: 'group',
          isStandard: true,
        }))
      ))
      .finally(() => setLoadingProps(false));
  }, [isOpen, selectedNavItem, teamId, groupKey]);

  useEffect(() => {
    if (!isOpen || selectedNavItem !== 'events') return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingEvents(true);
    axiosInstance
      .get('/events/summaries', { params: { team_id: numericTeamId } })
      .then((res) => {
        const summaries = res.data?.summaries || [];
        const names = [...new Set(summaries.map((s) => s.event_name).filter(Boolean))].sort();
        setEventNames(names);
      })
      .catch(() => setEventNames([]))
      .finally(() => setLoadingEvents(false));
  }, [isOpen, selectedNavItem, teamId]);

  useEffect(() => {
    if (!isOpen || selectedNavItem !== 'cohorts') return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingCohorts(true);
    axiosInstance
      .get('/cohorts', { params: { team_id: numericTeamId, include_user_count: false } })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.cohorts)) {
          setCohorts(res.data.cohorts);
        } else {
          setCohorts([]);
        }
      })
      .catch(() => setCohorts([]))
      .finally(() => setLoadingCohorts(false));
  }, [isOpen, selectedNavItem, teamId]);

  if (!isOpen) return null;

  const handleGroupPropertyClick = (prop) => {
    const newFilter = {
      id: existingFilter?.id || generateId(),
      category: 'group',
      propertySource: 'group',
      connector: existingFilter?.connector,
      field: prop.name,
      property: prop.name,
      propertyLabel: prop.label || prop.name,
      propertyType: prop.type || 'string',
      operator: getDefaultGroupOperator(prop.type || 'string'),
      value: null,
      value2: null,
    };
    onAdd(newFilter);
    onClose();
  };

  const handleEventClick = (eventLabel) => {
    const field = `event_count:${eventLabel}`;
    const newFilter = {
      id: existingFilter?.id || generateId(),
      propertySource: 'event',
      category: 'event',
      connector: existingFilter?.connector,
      field,
      property: field,
      propertyLabel: eventLabel,
      propertyType: 'number',
      operator: 'greater_than',
      value: 1,
      value2: null,
      didOperator: existingFilter?.didOperator || 'did',
      eventTimeFilter: existingFilter?.eventTimeFilter
        ? { ...existingFilter.eventTimeFilter }
        : { ...DEFAULT_WHO_TIME_FILTER },
    };
    onAdd(newFilter);
    onClose();
  };

  const handleCohortClick = (cohort) => {
    onAdd({
      id: existingFilter?.id || generateId(),
      category: 'cohort',
      propertySource: 'cohort',
      field: 'cohort',
      value: [cohort.id],
      propertyLabel: cohort.name,
      operator: 'is',
      connector: existingFilter?.connector ?? 'AND',
    });
    onClose();
  };

  const filteredEvents = eventNames.filter(
    (n) => !searchTerm || n.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredGroupProps = groupProperties.filter(
    (p) =>
      (p.label || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredCohorts = cohorts.filter(
    (c) => !searchTerm || (c.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'events', label: 'Events' },
    { id: 'properties', label: 'Properties' },
    { id: 'cohorts', label: 'Cohorts' },
  ];

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '400px',
        maxHeight: '420px',
        fontFamily: 'Raleway, sans-serif',
      }}
    >
      <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
        <div className="relative flex-1 mr-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#475569]" />
          <input
            type="text"
            placeholder={
              selectedNavItem === 'events'
                ? 'Search events…'
                : selectedNavItem === 'properties'
                  ? 'Search properties…'
                  : 'Search cohorts…'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#0f172a]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button type="button" onClick={onClose} className="p-2 text-[#475569] hover:text-[#0f172a]" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-28 border-r border-gray-200 bg-gray-50 flex-shrink-0 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setSelectedNavItem(tab.id);
                setSearchTerm('');
              }}
              className={`w-full text-left px-2 py-1.5 text-xs transition-colors ${
                selectedNavItem === tab.id
                  ? 'bg-white border-l-2 border-[#4b57aa] text-[#4b57aa] font-semibold'
                  : 'text-[#475569] hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-2 flex flex-col">
          {selectedNavItem === 'cohorts' ? (
            <>
              {loadingCohorts ? (
                <div className="p-4 text-sm text-[#475569] text-center">Loading cohorts…</div>
              ) : cohorts.length === 0 ? (
                <div className="p-4 text-sm text-[#475569] text-center">No cohorts. Create cohorts in the Cohorts page.</div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-0.5">
                  {filteredCohorts.map((cohort) => (
                    <button
                      key={cohort.id}
                      type="button"
                      onClick={() => handleCohortClick(cohort)}
                      className="w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 hover:bg-gray-50"
                    >
                      <Users className="h-4 w-4 text-[#475569] flex-shrink-0" />
                      <span className="flex-1">{cohort.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : selectedNavItem === 'events' ? (
            loadingEvents ? (
              <div className="p-4 text-sm text-[#475569] text-center">Loading…</div>
            ) : filteredEvents.length === 0 ? (
              <div className="p-4 text-sm text-[#475569] text-center">
                {searchTerm ? 'No events match.' : 'No events.'}
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredEvents.map((name) => {
                  const IconC = getDataTypeIcon('number');
                  const field = `event_count:${name}`;
                  const isSelected =
                    existingFilter && (existingFilter.field === field || existingFilter.property === field);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => handleEventClick(name)}
                      className={`w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 ${
                        isSelected ? 'bg-gray-200 border border-gray-300' : 'hover:bg-gray-50'
                      }`}
                    >
                      <IconC className="h-4 w-4 text-[#475569] flex-shrink-0" />
                      <span className="flex-1">{name}</span>
                      <span
                        className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569]"
                        title="Event"
                      >
                        E
                      </span>
                      {isSelected && <span className="text-xs text-[#475569]">(current)</span>}
                    </button>
                  );
                })}
              </div>
            )
          ) : loadingProps ? (
            <div className="p-4 text-sm text-[#475569] text-center">Loading…</div>
          ) : filteredGroupProps.length === 0 ? (
            <div className="p-4 text-sm text-[#475569] text-center">
              {searchTerm ? 'No properties match.' : 'No properties.'}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredGroupProps.map((prop, idx) => {
                const IconC = getDataTypeIcon(prop.type || 'string');
                const isSelected =
                  existingFilter &&
                  (existingFilter.field === prop.name || existingFilter.property === prop.name);
                return (
                  <button
                    key={`${prop.name}-${idx}`}
                    type="button"
                    onClick={() => handleGroupPropertyClick(prop)}
                    className={`w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 ${
                      isSelected ? 'bg-gray-200 border border-gray-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <IconC className="h-4 w-4 text-[#475569] flex-shrink-0" />
                    <span className="flex-1">{prop.label}</span>
                    <span
                      className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569]"
                      title="Group property"
                    >
                      G
                    </span>
                    {isSelected && <span className="text-xs text-[#475569]">(current)</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GroupPropertyFilterPopup;
